import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, BookOpen } from 'lucide-react';
import SEO from '@/components/common/SEO';
import CalculatorShell from '@/components/financial/CalculatorShell';
import { getCalculatorBySlug } from '@/content/financial/calculators';
import { getCaseStudyBySlug } from '@/content/financial/case-studies';

const BASE_URL = 'https://koreanhub.net';

export default function CalculatorDetail() {
  const { slug } = useParams<{ slug: string }>();
  const def = getCalculatorBySlug(slug ?? '');

  if (!def) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <SEO
          title="Calculator not found"
          description="This calculator does not exist."
          path={`/financial/calculators/${slug ?? ''}`}
          noindex
        />
        <h1 className="text-2xl font-bold text-ink-900 dark:text-ink-50">Calculator not found</h1>
        <p className="mt-3 text-ink-500 dark:text-ink-400">
          <Link
            to="/financial/calculators"
            className="font-medium text-cheong-500 underline dark:text-cheong-dark"
          >
            Browse all calculators
          </Link>
        </p>
      </div>
    );
  }

  const related = (def.relatedCaseStudySlugs ?? [])
    .map((s) => getCaseStudyBySlug(s))
    .filter((s): s is NonNullable<typeof s> => Boolean(s));

  const appSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: def.name,
    description: def.summary,
    applicationCategory: 'FinanceApplication',
    operatingSystem: 'Any',
    url: `${BASE_URL}/financial/calculators/${def.slug}`,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  };

  return (
    <div className="bg-white dark:bg-surface-dark">
      <SEO
        title={def.name}
        description={def.summary}
        path={`/financial/calculators/${def.slug}`}
        jsonLd={appSchema}
      />

      <header className="border-b border-ink-200 bg-ink-50 dark:border-surface-dark-border dark:bg-white/[0.02]">
        <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
          <Link
            to="/financial/calculators"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-500 hover:text-ink-900 dark:text-ink-400 dark:hover:text-ink-50"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Calculators
          </Link>
          <h1 className="mt-4 text-3xl font-bold leading-tight text-ink-900 dark:text-ink-50">
            {def.name}
          </h1>
          <p lang="ko" className="mt-1 font-korean text-base text-ink-500 dark:text-ink-400">
            {def.nameKo}
          </p>
          <p className="mt-3 max-w-2xl text-base leading-7 text-ink-700 dark:text-ink-200">
            {def.summary}
          </p>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <CalculatorShell def={def} />

        {/* ── How to read the output ── */}
        <section className="mt-10 rounded-xl border border-ink-200 bg-ink-50 p-5 dark:border-surface-dark-border dark:bg-white/[0.03] sm:p-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-ink-900 dark:text-ink-50">
            <BookOpen className="h-4 w-4 text-cheong-500 dark:text-cheong-dark" aria-hidden="true" />
            How to read this
          </h2>
          <p className="mt-3 max-w-3xl text-base leading-7 text-ink-700 dark:text-ink-200">
            {def.interpretation}
          </p>
        </section>

        {/* ── Worked examples ── */}
        {related.length > 0 && (
          <section className="mt-10">
            <h2 className="mb-4 text-lg font-semibold text-ink-900 dark:text-ink-50">
              Seen in practice
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {related.map((study) => (
                <Link
                  key={study.slug}
                  to={`/financial/case-studies/${study.slug}`}
                  className="rounded-xl border border-ink-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md dark:border-surface-dark-border dark:bg-surface-dark-card"
                >
                  <span className="block text-sm font-bold text-ink-900 dark:text-ink-50">
                    {study.title}
                  </span>
                  <span className="mt-1.5 block text-xs leading-5 text-ink-500 dark:text-ink-400">
                    {study.dek}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
